import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Space, Col, Row, Spin, Empty, Modal, Descriptions } from "antd";
import ReusableTable from "../../../components/table";
import useSaveUnit from "../../../hooks/landlord/property/useSaveUnit";

const DashPage = () => {
  const navigate = useNavigate();
  const { getAllUnits, loading } = useSaveUnit();
  const [tableData, setTableData] = useState([]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState(null);

  const handleView = (record) => {
    setSelectedUnit(record);
    setIsModalOpen(true);
  };

  const columns = [
    {
      title: "Unit",
      dataIndex: "unitnumber",
      key: "unitnumber",
    },
    {
      title: "Rent",
      dataIndex: "rent",
      key: "rent",
      render: (text) => `$${text || "N/A"}`,
    },
    {
      title: "Move In",
      dataIndex: "dateAvailable",
      key: "dateAvailable",
    },
    {
      title: "Beds",
      dataIndex: "bedrooms",
      key: "bedrooms",
    },
    {
      title: "Baths",
      dataIndex: "bathrooms",
      key: "bathrooms",
    },
    {
      title: "Size (sqft)",
      dataIndex: "size",
      key: "size",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
    },
  ];

  useEffect(() => {
    const fetchData = async () => {
      const allUnits = await getAllUnits();
      const formatted = allUnits.map((unit) => ({
        ...unit,
        key: unit.id,
        unit: unit.unit || unit.title?.split(" ")[0],
      }));
      setTableData(formatted);
    };

    fetchData();
  }, []);

  return (
    <>
      <Space
        direction="vertical"
        size={32}
        style={{ width: "100%", paddingTop: "0px" }}
      >
        <Row gutter={20}>
          <Col span={24}>
            {loading ? (
              <div style={{ textAlign: "center", marginTop: 40 }}>
                <Spin size="large" tip="Loading property listings..." />
              </div>
            ) : tableData.length === 0 ? (
              <p style={{ textAlign: "center", marginTop: 20 }}>
                No property listings found
              </p>
            ) : (
              <ReusableTable
                columns={columns}
                data={tableData}
                showView={true}
                showEdit={true}
                showDelete={false}
                onView={handleView}
                onEdit={(record) =>
                  navigate(`/landlord/property/unit/${record.id}`)
                }
                onDelete={(record) => console.log("Delete", record)}
                pagination={{ pageSize: 10 }}
              />
            )}
          </Col>
        </Row>
      </Space>

      <Modal
        title="Property Details"
        visible={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={null}
        width={800}
      >
        {selectedUnit && (
          <Descriptions bordered column={1} size="middle">
            <Descriptions.Item label="Title">
              {selectedUnit.title}
            </Descriptions.Item>
            <Descriptions.Item label="Rent">
              ${selectedUnit.rent}
            </Descriptions.Item>
            <Descriptions.Item label="Deposit">
              ${selectedUnit.deposit}
            </Descriptions.Item>
            <Descriptions.Item label="Beds">
              {selectedUnit.bedrooms}
            </Descriptions.Item>
            <Descriptions.Item label="Baths">
              {selectedUnit.bathrooms}
            </Descriptions.Item>
            <Descriptions.Item label="Size">
              {selectedUnit.size} sqft
            </Descriptions.Item>
            <Descriptions.Item label="Move In Date">
              {selectedUnit.dateAvailable}
            </Descriptions.Item>
            <Descriptions.Item label="Lease Duration">
              {selectedUnit.leaseDuration}
            </Descriptions.Item>
            <Descriptions.Item label="Parking Type">
              {selectedUnit.parkingType}
            </Descriptions.Item>
            <Descriptions.Item label="Parking Fee">
              ${selectedUnit.parkingFee}
            </Descriptions.Item>
            <Descriptions.Item label="Move-in Fee">
              ${selectedUnit.moveInFee}
            </Descriptions.Item>
            <Descriptions.Item label="Pets Allowed">
              {selectedUnit.petsAllowed ? "Yes" : "No"}
            </Descriptions.Item>
            <Descriptions.Item label="Pet Options">
              {Object.entries(selectedUnit.petOptions || {})
                .filter(([, allowed]) => allowed)
                .map(([pet]) => pet)
                .join(", ")}
            </Descriptions.Item>
            <Descriptions.Item label="Amenities">
              {(selectedUnit.amenities || []).join(", ")}
            </Descriptions.Item>
            <Descriptions.Item label="Utilities">
              {(selectedUnit.utilities || []).join(", ")}
            </Descriptions.Item>
            <Descriptions.Item label="Showing Date">
              {selectedUnit.showingDateAvailable || "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label="Showing Time">
              {selectedUnit.showingTimeAvailableFrom} -{" "}
              {selectedUnit.showingTimeAvailableTo}
            </Descriptions.Item>
            <Descriptions.Item label="Showing Duration">
              {selectedUnit.showingDuration || "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              {selectedUnit.status}
            </Descriptions.Item>
            <Descriptions.Item label="Description">
              {selectedUnit.description}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </>
  );
};

export default DashPage;
